import { useCase } from "src/context/case-context";
import GeneralFallbackView from "src/pages/case-landing/views/general-fallback/general-fallback-view";
import NoRedressDecisionView from "src/pages/case-landing/views/no-redress-decision/no-redress-decision-view";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import CaseClosedView from "src/pages/case-landing/views/case-closed/case-closed-view";
import CaseInAssessmentView from "src/pages/case-landing/views/case-in-assessment/case-in-assessment-view";
import SubmitObjectionView from "src/pages/case-landing/views/submit-objection/submit-objection-view";
import { PortalState } from "src/api/types/enums";

export enum CaseInfoBannerKey {
  BeforeYouContinue = "beforeYouContinue",
  InProgress = "inProgress",
  NotIncluded = "notIncluded",
  UpdateSent = "updateSent",
  TryAgainLater = "tryAgainLater",
  DecisionIssued = "decisionIssued",
  CaseClosed = "caseClosed",
  OptedOut = "optedOut",
  StartFormalObjection = "startFormalObjection",
  ObjectionReview = "objectionReview",
  ArrangingPayment = "arrangingPayment",
  Unknown = "unknown",
  Cancelled = "cancelled",
  CaseOnHold = "caseOnHold",
}

export type CaseLandingPageProps =
  | {
      showOptInOut: boolean;
      view: ReactNode;
      standardFaq: boolean;
    }
  | {
      showOptInOut: undefined;
      view: ReactNode;
      standardFaq: undefined;
    };

export type CaseLandingViewProps = {
  bannerKey?: CaseInfoBannerKey;
};

export function useCaseLandingView(): CaseLandingPageProps {
  const { customerData } = useCase();
  if (!customerData) {
    return {
      showOptInOut: undefined,
      view: <Navigate to={"/"} />,
      standardFaq: undefined
    };
  }
  const status = customerData.portalState;
  switch (status) {
    case PortalState.NoRedressStart:{
      return {
        showOptInOut: true,
        view: <NoRedressDecisionView state={status} />,
        standardFaq: true
      };
    }
    case PortalState.NoRedressFinal: {
      return {
        showOptInOut: true,
        view: <NoRedressDecisionView state={status} />,
        standardFaq: false
      };
    }
    case PortalState.ChallengeIntent: {
      return {
        showOptInOut: true,
        view: <SubmitObjectionView state={status} />,
        standardFaq: true,
      };
    }
    case PortalState.ChallengeSubmitted:
    case PortalState.NoRedressAssessment: {
      return {
        showOptInOut: true,
        view: <CaseInAssessmentView state={status} />,
        standardFaq: true,
      };
    }
    case PortalState.OptedOut:
    case PortalState.NoRedressClosed: {
      return {
        showOptInOut: false,
        view: <CaseClosedView state={status} />,
        standardFaq: true,
      };
    }
    case PortalState.Unknown:
    case PortalState.Cancelled: {
      return {
        showOptInOut: false,
        view: <GeneralFallbackView state={status} />,
        standardFaq: true,
      };
    }
    default: {
      return {
        showOptInOut: false,
        view: <GeneralFallbackView state={status} />,
        standardFaq: true,
      };
    }
  }
}
